import React from "react";
import { Group, Text, Anchor, SimpleGrid, Tooltip } from "@mantine/core";
import { Mail, AtSign, GraduationCap, AlertTriangle } from "lucide-react";
import { normalizeHandle, handleWarning } from "../utils";

/** The contact handles a registration and a student have in common. */
export interface ContactFields {
  phystech_email: string;
  gmail: string | null;
  vk_id: string | null;
  mipt_tech_tag: string | null;
}

// Handles are normalized server-side to the bare id; be defensive anyway.
export function vkHref(v: string): string {
  if (/^https?:\/\//i.test(v)) return v;
  return `https://vk.com/${normalizeHandle(v)}`;
}
export function miptHref(v: string): string {
  return `https://mipt.tech/profile/${normalizeHandle(v)}`;
}

export function ContactLink({ icon, label, href, warning }: {
  icon: React.ReactNode; label: string; href: string; warning?: string | null;
}) {
  const external = !href.startsWith("mailto:");
  return (
    <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
      <Text c="dimmed" style={{ display: "flex", flexShrink: 0 }}>{icon}</Text>
      <Anchor
        href={href}
        fz="xs"
        truncate
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {label}
      </Anchor>
      {warning && (
        <Tooltip label={warning} withArrow multiline w={220}>
          <Text c="yellow" style={{ display: "flex", flexShrink: 0 }}><AlertTriangle size={13} /></Text>
        </Tooltip>
      )}
    </Group>
  );
}

/** Contact handles as two rows of icon links, shared by the registrations and users tables. */
export function Contacts({ c }: { c: ContactFields }) {
  return (
    <SimpleGrid cols={2} spacing="xs" verticalSpacing={4}>
      {c.gmail && <ContactLink icon={<Mail size={14} />} label={c.gmail} href={`mailto:${c.gmail}`} />}
      <ContactLink icon={<GraduationCap size={14} />} label={c.phystech_email} href={`mailto:${c.phystech_email}`} />
      {c.mipt_tech_tag && <ContactLink icon={<GraduationCap size={14} />} label={normalizeHandle(c.mipt_tech_tag)} href={miptHref(c.mipt_tech_tag)} warning={handleWarning(c.mipt_tech_tag)} />}
      {c.vk_id && <ContactLink icon={<AtSign size={14} />} label={normalizeHandle(c.vk_id)} href={vkHref(c.vk_id)} warning={handleWarning(c.vk_id)} />}
    </SimpleGrid>
  );
}
